import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Truck, MapPin, Briefcase, Zap, ChevronLeft, Lock, ArrowUpRight, ArrowDownRight,
  Package, Send, Check, X, Thermometer, Sun, CloudRain, Wind, CloudLightning, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'hg_founder_token';

const money0 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;
const CARRIERS = ['NZ Post', 'CourierPost', 'Aramex', 'Australia Post', 'DHL'];

const BADGE = {
  pending: { bg: '#FBEFD0', fg: '#8A6D2B', label: 'PENDING' },
  processing: { bg: '#E1ECF6', fg: '#2E5C86', label: 'PROCESSING' },
  shipped: { bg: '#E1F0D6', fg: '#3E6B24', label: 'SHIPPED' },
  delivered: { bg: '#ECEDEA', fg: '#5C665C', label: 'DELIVERED' },
};
const URGENCY = {
  high: { bg: '#F6DFD6', fg: '#B5482A' },
  medium: { bg: '#FBEFD0', fg: '#8A6D2B' },
  low: { bg: '#E1F0D6', fg: '#3E6B24' },
};
const TIP_ICON = { thermometer: Thermometer, sun: Sun, 'cloud-rain': CloudRain, wind: Wind, package: Package };

const fapi = (token) => axios.create({ baseURL: `${API}/founder`, headers: { 'X-Founder-Token': token } });

export default function SupplierCommand() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [tab, setTab] = useState('dispatch');

  const onAuthErr = useCallback((e) => {
    if (e?.response?.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken('');
      return true;
    }
    return false;
  }, []);

  if (!token) return <VaultGate onUnlock={(t) => { sessionStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;

  const TABS = [
    { key: 'dispatch', label: 'Dispatch', icon: Truck },
    { key: 'demand', label: 'Demand Map', icon: MapPin },
    { key: 'partners', label: 'Partner ROI', icon: Briefcase },
    { key: 'ai', label: 'AI Tips', icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="supplier-command">
      {/* Command header */}
      <div className="bg-[#0F2D1F] text-[#FAF6EC] px-6 py-5">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <button onClick={() => window.history.back()} className="w-10 h-10 rounded-full bg-white/10 grid place-items-center hover:bg-white/20 transition" data-testid="cmd-back">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <p className="text-xs font-bold tracking-widest text-[#B4D330]">B2B LOGISTICS HUB</p>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Supplier Command Center</h1>
          </div>
          <div className="flex items-center gap-2 bg-[#B4D330]/15 rounded-full px-3 py-1.5">
            <span className="w-2 h-2 rounded-full bg-[#B4D330] animate-pulse" />
            <span className="text-xs font-semibold text-[#B4D330]">LIVE</span>
          </div>
          <button onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(''); }} className="text-xs text-white/60 hover:text-white ml-2" data-testid="cmd-lock">Lock</button>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto flex gap-2 mt-4 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon; const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cmd-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${active ? 'bg-[#B4D330] text-[#0F2D1F]' : 'bg-white/10 text-[#FAF6EC] hover:bg-white/20'}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {tab === 'dispatch' && <FulfillmentHud token={token} onAuthErr={onAuthErr} />}
        {tab === 'demand' && <DemandMap token={token} onAuthErr={onAuthErr} />}
        {tab === 'partners' && <PartnerRoi token={token} onAuthErr={onAuthErr} />}
        {tab === 'ai' && <StockingTips token={token} onAuthErr={onAuthErr} />}
      </div>
    </div>
  );
}

/* ---------------- Vault gate ---------------- */
function VaultGate({ onUnlock }) {
  const [stage, setStage] = useState('creds'); // creds -> code
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const requestCode = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/founder/request-code`, { email: email.trim(), secondary_password: pw });
      if (r.data?.dev_code) setDevCode(r.data.dev_code);
      setStage('code');
    } catch (e) { setError(e?.response?.data?.detail || 'Could not verify those credentials.'); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/founder/verify-code`, { email: email.trim(), code: code.trim() });
      onUnlock(r.data.founder_token);
    } catch (e) { setError(e?.response?.data?.detail || 'Invalid or expired code.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0F2D1F] grid place-items-center px-6" data-testid="cmd-vault-gate">
      <div className="w-full max-w-sm bg-[#FAF6EC] rounded-3xl p-8 shadow-2xl">
        <div className="w-14 h-14 rounded-2xl bg-[#1A4331] grid place-items-center mb-5"><Lock className="w-6 h-6 text-[#B4D330]" /></div>
        <h1 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Founder&rsquo;s Vault</h1>
        <p className="text-sm text-[#5C665C] mt-1 mb-6">Supplier Command Center is founder-only. Sign in with your vault credentials.</p>

        {stage === 'creds' ? (
          <>
            <label className="text-xs font-semibold text-[#2D241E]">Founder email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com" autoComplete="off"
              className="w-full mt-1 mb-3 px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 focus:outline-none focus:border-[#1A4331]/30" data-testid="vault-email" />
            <label className="text-xs font-semibold text-[#2D241E]">Vault password</label>
            <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" placeholder="••••••••"
              onKeyDown={(e) => e.key === 'Enter' && email && pw && requestCode()}
              className="w-full mt-1 px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 focus:outline-none focus:border-[#1A4331]/30" data-testid="vault-password" />
            {error && <p className="text-sm text-[#C66B4A] mt-3">{error}</p>}
            <button onClick={requestCode} disabled={!email || !pw || busy}
              className="w-full mt-5 bg-[#1A4331] hover:bg-[#255C44] disabled:opacity-50 text-white font-semibold py-3 rounded-full transition" data-testid="vault-continue">
              {busy ? 'Verifying…' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            <label className="text-xs font-semibold text-[#2D241E]">6-digit code</label>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} placeholder="••••••"
              onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && verify()}
              className="w-full mt-1 px-4 py-3 bg-white rounded-xl border border-[#1A4331]/10 tracking-[0.4em] text-center text-lg focus:outline-none focus:border-[#1A4331]/30" data-testid="vault-code" />
            {devCode && <p className="text-xs text-[#5C665C] mt-2" data-testid="vault-dev-code">Preview code: <b>{devCode}</b></p>}
            {error && <p className="text-sm text-[#C66B4A] mt-3">{error}</p>}
            <button onClick={verify} disabled={code.length !== 6 || busy}
              className="w-full mt-5 bg-[#1A4331] hover:bg-[#255C44] disabled:opacity-50 text-white font-semibold py-3 rounded-full transition" data-testid="vault-verify">
              {busy ? 'Unlocking…' : 'Unlock command center'}
            </button>
            <button onClick={() => { setStage('creds'); setCode(''); setError(''); }} className="w-full mt-2 text-sm text-[#5C665C] hover:text-[#1A4331]">Back</button>
          </>
        )}
      </div>
    </div>
  );
}

const Card = ({ children, className = '', ...p }) => (
  <div className={`bg-white rounded-2xl border border-[#1A4331]/10 ${className}`} {...p}>{children}</div>
);
const SummaryTile = ({ value, label, accent, small, testid }) => (
  <div className="flex-1 bg-white rounded-2xl border border-[#1A4331]/10 p-4 text-center" data-testid={testid}>
    <p className={`font-bold ${small ? 'text-2xl' : 'text-3xl'}`} style={{ color: accent, fontFamily: 'Outfit, sans-serif' }}>{value}</p>
    <p className="text-xs text-[#5C665C] mt-1">{label}</p>
  </div>
);
const Loader = () => <div className="grid place-items-center py-20"><div className="w-10 h-10 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" /></div>;

/* ---------------- Module 1 · Fulfillment ---------------- */
function FulfillmentHud({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sheet, setSheet] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await fapi(token).get(`/supplier/fulfillment?status=${filter}`); setData(r.data); }
    catch (e) { if (!onAuthErr(e)) setData(null); }
    finally { setLoading(false); }
  }, [token, filter, onAuthErr]);
  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};
  const chips = ['all', 'pending', 'processing', 'shipped', 'delivered'];

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <SummaryTile value={data?.to_action ?? 0} label="Need action" accent="#C66B4A" testid="fulfil-to-action" />
        <SummaryTile value={counts.shipped ?? 0} label="In transit" accent="#4E7A33" />
        <SummaryTile value={money0(data?.open_revenue)} label="Open revenue" accent="#1A4331" small />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 mb-2">
        {chips.map((c) => {
          const active = filter === c; const n = c === 'all' ? data?.total_orders : counts[c];
          return (
            <button key={c} onClick={() => setFilter(c)} data-testid={`fulfil-filter-${c}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition ${active ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-[#1A4331] border-[#1A4331]/20 hover:border-[#1A4331]/40'}`}>
              {c[0].toUpperCase() + c.slice(1)}{n != null ? ` ${n}` : ''}
            </button>
          );
        })}
      </div>

      {loading ? <Loader /> : (data?.orders || []).length === 0 ? (
        <Card className="p-10 text-center text-[#5C665C]"><Package className="w-6 h-6 mx-auto mb-2" />No orders in this view.</Card>
      ) : (
        <Card className="overflow-hidden">
          {data.orders.map((o, i) => {
            const b = BADGE[o.badge] || BADGE.pending;
            return (
              <button key={o.order_id} onClick={() => setSheet(o)} data-testid={`fulfil-order-${i}`}
                className={`w-full flex items-start gap-4 p-4 text-left hover:bg-[#F0F2EB]/60 transition ${i > 0 ? 'border-t border-[#1A4331]/8' : ''}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#1A4331]">#{o.order_no}</span>
                    <span className="rounded px-2 py-0.5 text-[10px] font-bold tracking-wide" style={{ background: b.bg, color: b.fg }}>{b.label}</span>
                  </div>
                  <p className="text-sm text-[#2A3528] mt-0.5 truncate">{o.buyer_name} · {o.region}</p>
                  <p className="text-sm text-[#5C665C] truncate">{o.items_summary}</p>
                  {o.tracking?.number && <p className="text-xs text-[#4E7A33] mt-0.5 truncate">{o.tracking.carrier} · {o.tracking.number}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold text-[#1A4331]">{money0(o.total)}</p>
                  <p className="text-xs text-[#5C665C]">{o.age_days}d ago</p>
                  <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-[#1A4331] bg-[#1A4331]/6 rounded-full px-2.5 py-1">Manage</span>
                </div>
              </button>
            );
          })}
        </Card>
      )}

      {sheet && <FulfilSheet order={sheet} token={token} onAuthErr={onAuthErr} onClose={() => setSheet(null)} onDone={(m) => { setSheet(null); setToast(m); setTimeout(() => setToast(''), 2800); load(); }} />}
      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1A4331] text-white text-sm font-semibold px-4 py-3 rounded-xl flex items-center gap-2 shadow-lg z-50" data-testid="cmd-toast"><Check className="w-4 h-4" />{toast}</div>}
    </div>
  );
}

function FulfilSheet({ order, token, onClose, onDone, onAuthErr }) {
  const [stage, setStage] = useState(order.badge === 'pending' ? 'processing' : order.badge);
  const [tracking, setTracking] = useState(order.tracking?.number || '');
  const [carrier, setCarrier] = useState(order.tracking?.carrier || 'NZ Post');
  const [busy, setBusy] = useState(false);
  const needsTracking = stage === 'shipped' || stage === 'delivered';

  const submit = async () => {
    setBusy(true);
    try {
      await fapi(token).post(`/supplier/orders/${order.order_id}/fulfil`, {
        status: stage, tracking_number: needsTracking ? tracking.trim() : '', carrier: needsTracking ? carrier : '',
      });
      onDone(`Order #${order.order_no} → ${stage}`);
    } catch (e) { if (!onAuthErr(e)) onDone(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#0F2D1F]/55 grid place-items-center z-50 px-6" onClick={onClose}>
      <div className="w-full max-w-md bg-[#FAF9F6] rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="fulfil-sheet">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Update order #{order.order_no}</h3>
          <button onClick={onClose} className="text-[#5C665C] hover:text-[#1A4331]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#5C665C] mb-4">{order.buyer_name} · {order.region} · {money0(order.total)}</p>

        <p className="text-xs font-semibold text-[#2D241E] mb-2">SET STATUS</p>
        <div className="flex gap-2">
          {['processing', 'shipped', 'delivered'].map((s) => {
            const active = stage === s; const b = BADGE[s];
            return (
              <button key={s} onClick={() => setStage(s)} data-testid={`fulfil-stage-${s}`}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${active ? 'text-white border-transparent' : 'bg-white text-[#1A4331] border-[#1A4331]/20'}`}
                style={active ? { background: b.fg } : {}}>{b.label}</button>
            );
          })}
        </div>

        {needsTracking && (
          <>
            <p className="text-xs font-semibold text-[#2D241E] mt-4 mb-2">CARRIER</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {CARRIERS.map((c) => (
                <button key={c} onClick={() => setCarrier(c)} data-testid={`fulfil-carrier-${c.replace(/[^a-zA-Z]/g, '')}`}
                  className={`px-3.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition ${carrier === c ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-[#1A4331] border-[#1A4331]/20'}`}>{c}</button>
              ))}
            </div>
            <p className="text-xs font-semibold text-[#2D241E] mt-4 mb-2">TRACKING NUMBER</p>
            <input value={tracking} onChange={(e) => setTracking(e.target.value.toUpperCase())} placeholder="e.g. NZ123456789"
              className="w-full px-4 py-3 bg-white rounded-xl border border-[#1A4331]/20 focus:outline-none focus:border-[#1A4331]/40" data-testid="fulfil-tracking-input" />
          </>
        )}

        <button onClick={submit} disabled={busy}
          className="w-full mt-5 bg-[#1A4331] hover:bg-[#255C44] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 transition" data-testid="fulfil-submit">
          {stage === 'shipped' ? <Send className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {busy ? 'Saving…' : stage === 'shipped' ? 'Mark shipped & add tracking' : `Set ${stage}`}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Module 2 · Demand Map ---------------- */
const MAP_BOUNDS = { minLng: 112, maxLng: 180, minLat: -47.5, maxLat: -25 };
function DemandMap({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const W = 760, H = 380, pad = 34;
  useEffect(() => {
    fapi(token).get('/supplier/demand-map?weeks_ahead=8').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <Loader />;
  if (!data) return <Card className="p-10 text-center text-[#5C665C]">Demand map unavailable.</Card>;
  const pts = data.points || [];
  const project = (lat, lng) => ({
    x: pad + ((lng - MAP_BOUNDS.minLng) / (MAP_BOUNDS.maxLng - MAP_BOUNDS.minLng)) * (W - pad * 2),
    y: pad + ((MAP_BOUNDS.maxLat - lat) / (MAP_BOUNDS.maxLat - MAP_BOUNDS.minLat)) * (H - pad * 2),
  });

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.total_growers}</p>
          <p className="text-sm text-[#5C665C]">growers sowing · next {data.weeks_ahead} weeks</p>
        </div>
        {data.hottest && (
          <div className="text-right">
            <p className="text-xs text-[#5C665C]">Hottest region</p>
            <p className="font-semibold text-[#1A4331]">{data.hottest.region}</p>
            <p className="text-sm text-[#4E7A33]">{data.hottest.top_crop} leads</p>
          </div>
        )}
      </div>

      <div className="rounded-2xl overflow-hidden mb-6">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ display: 'block' }}>
          <defs>
            <radialGradient id="wheat" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#B4D330" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#B4D330" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width={W} height={H} rx="16" fill="#0F2D1F" />
          {pts.map((p, i) => { const { x, y } = project(p.lat, p.lng); const glow = 24 + p.intensity * 70; return <circle key={`g${i}`} cx={x} cy={y} r={glow} fill="url(#wheat)" opacity={0.3 + p.intensity * 0.45} />; })}
          {pts.map((p, i) => {
            const { x, y } = project(p.lat, p.lng); const r = 5 + p.intensity * 9;
            return (
              <g key={`c${i}`}>
                <circle cx={x} cy={y} r={r} fill="#B4D330" stroke="#0F2D1F" strokeWidth="2" />
                <text x={x} y={y - r - 6} fill="#FAF6EC" fontSize="11" textAnchor="middle" opacity="0.85" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.region}</text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-semibold text-[#5C665C] mb-2">REGIONS BY DEMAND</p>
          <Card className="overflow-hidden">
            {pts.map((p, i) => (
              <div key={i} className={`flex items-center gap-3 p-3.5 ${i > 0 ? 'border-t border-[#1A4331]/8' : ''}`} data-testid={`demand-region-${i}`}>
                <span className="text-sm text-[#5C665C] w-5">{i + 1}</span>
                <div className="flex-1">
                  <p className="font-semibold text-[#1A4331]">{p.region}</p>
                  <div className="h-1.5 rounded-full bg-[#D9E1CB] mt-1 overflow-hidden"><div className="h-full rounded-full bg-[#6B9F4A]" style={{ width: `${Math.max(p.intensity * 100, 4)}%` }} /></div>
                </div>
                <div className="text-right w-28">
                  <p className="text-sm font-semibold text-[#1A4331]">{p.growers} growers</p>
                  <p className="text-xs text-[#4E7A33] truncate">{p.top_crop}</p>
                </div>
              </div>
            ))}
          </Card>
        </div>
        <div>
          <p className="text-xs font-semibold text-[#5C665C] mb-2">MOST-WANTED VARIETIES</p>
          <div className="flex flex-wrap gap-2">
            {(data.top_varieties || []).map((v, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-[#1A4331]/15 rounded-full px-3.5 py-2 text-sm font-semibold text-[#1A4331]">{v.crop}<span className="text-[#5C665C] font-normal">{v.growers}</span></span>
            ))}
          </div>
          <p className="text-sm text-[#5C665C] mt-4">{data.seeded ? 'Blended with a demo baseline until live planting volume grows.' : "Live 'sowing intent' from grower Season Planners."}</p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Module 3 · Partner ROI ---------------- */
function PartnerRoi({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fapi(token).get('/supplier/partner-roi').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <Loader />;
  if (!data) return <Card className="p-10 text-center text-[#5C665C]">Partner data unavailable.</Card>;
  const t = data.totals || {}; const series = data.series || [];
  const max = Math.max(...series, 1); const W = 640, H = 90, pad = 6;
  const step = series.length > 1 ? (W - pad * 2) / (series.length - 1) : 0;
  const line = series.map((v, i) => `${pad + step * i},${pad + (H - pad * 2) * (1 - v / max)}`).join(' ');

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <SummaryTile value={money0(t.gmv)} label="Partner GMV" accent="#1A4331" small />
        <SummaryTile value={money0(t.commission)} label="Our commission" accent="#C9A85B" small />
        <SummaryTile value={t.giftcard_units ?? 0} label="Gift cards" accent="#4E7A33" />
      </div>

      <Card className="p-4 mb-6">
        <p className="text-xs font-semibold text-[#5C665C] mb-3">8-WEEK PARTNER SALES</p>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ display: 'block' }}>
          <polyline points={line} fill="none" stroke="#4E7A33" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {series.map((v, i) => <circle key={i} cx={pad + step * i} cy={pad + (H - pad * 2) * (1 - v / max)} r="3" fill="#4E7A33" />)}
        </svg>
        <div className="flex justify-between border-t border-[#1A4331]/8 pt-3 mt-1">
          <div><p className="text-xs text-[#5C665C]">Gift-card revenue</p><p className="font-semibold text-[#1A4331]">{money0(t.giftcard_revenue)}</p></div>
          <div className="text-right"><p className="text-xs text-[#5C665C]">Branded merch</p><p className="font-semibold text-[#1A4331]">{money0(t.merch_revenue)} · {t.merch_units}u</p></div>
        </div>
      </Card>

      <p className="text-xs font-semibold text-[#5C665C] mb-2">BY BRAND</p>
      <Card className="overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2.5 bg-[#1A4331]/6 text-xs text-[#5C665C] font-semibold">
          <span className="flex-1">Brand</span><span className="w-24 text-right">Revenue</span><span className="w-20 text-right">Commission</span><span className="w-16 text-right">Trend</span>
        </div>
        {(data.brands || []).map((b, i) => {
          const up = b.trend_pct >= 0;
          return (
            <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-t border-[#1A4331]/8" data-testid={`partner-brand-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1A4331] truncate">{b.brand}</p>
                <p className="text-xs text-[#5C665C]">{b.kind} · {b.giftcard_units} cards · {b.merch_units} merch</p>
              </div>
              <span className="w-24 text-right font-semibold text-[#1A4331]">{money0(b.total_revenue)}</span>
              <span className="w-20 text-right font-semibold text-[#C9A85B]">{money0(b.commission)}</span>
              <span className={`w-16 text-right font-semibold inline-flex items-center justify-end gap-0.5 ${up ? 'text-[#4E7A33]' : 'text-[#C66B4A]'}`}>
                {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{Math.abs(b.trend_pct)}%
              </span>
            </div>
          );
        })}
      </Card>
      <p className="text-sm text-[#5C665C] mt-4">Demo partner-sales data (labelled) until live gift-card &amp; merch feeds connect.</p>
    </div>
  );
}

/* ---------------- Module 4 · Stocking Tips ---------------- */
function StockingTips({ token, onAuthErr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fapi(token).get('/supplier/stocking-tips').then((r) => setData(r.data)).catch((e) => { if (!onAuthErr(e)) setData(null); }).finally(() => setLoading(false));
  }, [token, onAuthErr]);
  if (loading) return <div className="grid place-items-center py-16"><div className="w-10 h-10 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" /><p className="text-sm text-[#5C665C] mt-3">Reading the week&rsquo;s forecast…</p></div>;
  if (!data) return <Card className="p-10 text-center text-[#5C665C]">Stocking tips unavailable.</Card>;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><CloudLightning className="w-5 h-5 text-[#1A4331]" /><h3 className="font-semibold text-[#1A4331]">Weather-driven stocking tips</h3></div>
        <span className="bg-[#B4D330] text-[#0F2D1F] text-xs font-semibold rounded-full px-3 py-1">{data.source === 'ai' ? 'AI' : 'Rules'}</span>
      </div>
      <p className="text-sm text-[#5C665C] mb-5">What partners should stock up on before next week&rsquo;s weather hits.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {(data.tips || []).map((t, i) => {
          const Icon = TIP_ICON[t.icon] || Package; const u = URGENCY[t.urgency] || URGENCY.medium;
          return (
            <Card key={i} className="p-4 flex gap-4" data-testid={`stocking-tip-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#1A4331]/6 grid place-items-center shrink-0"><Icon className="w-5 h-5 text-[#1A4331]" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-[#1A4331] flex-1 truncate">{t.headline}</p>
                  <span className="rounded px-2 py-0.5 text-[10px] font-bold" style={{ background: u.bg, color: u.fg }}>+{t.spike_pct}%</span>
                </div>
                <p className="text-sm text-[#5C665C] mt-1 leading-snug">{t.detail}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="inline-flex items-center gap-1.5 bg-[#6B9F4A]/12 text-[#4E7A33] text-xs font-semibold rounded-full px-2.5 py-1"><Package className="w-3 h-3" />{t.product}</span>
                  <span className="text-xs text-[#5C665C]">{t.region}</span>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
